var React = require('react');
var { useState, useRef, useEffect } = React;
var PropTypes = require('prop-types');
var { copyTextToClipboard } = require('Utils');
var $ = require('jquery');
var dayjs = require('../../utils/dayjsConfig');

const COPIED_FEEDBACK_DURATION_MS = 1500;

const buildFieldDescription = ({
  distributionName,
  distributionId,
  farmingUnitName,
  farmingUnitId,
  fieldName,
  fieldId,
  userName,
  userEmail,
  farmingUnitUserEmails,
}) => {
  const now = dayjs().format('YYYY-MM-DD HH:mm');
  const userLine =
    userName && userEmail
      ? `${userName} (${userEmail})`
      : userName || userEmail || '-';
  const usersLine =
    farmingUnitUserEmails && farmingUnitUserEmails.length > 0
      ? farmingUnitUserEmails.join(', ')
      : '-';
  return (
    `Distribution: ${distributionName || '-'} (${distributionId || '-'})\n` +
    `Farming Unit: ${farmingUnitName || '-'} (${farmingUnitId || '-'})\n` +
    `Field: ${fieldName || '-'} (${fieldId || '-'})\n` +
    `User: ${userLine}\n` +
    `Permitted users: ${usersLine}\n` +
    `Date: ${now}`
  );
};

/**
 * Reusable Field Settings Menu Component
 * Used in both FieldsSelector (Field View) and IrrigationSystemMonitoring (Farm View)
 *
 * If onDelete/onArchive props are provided, they will be called directly after confirmation.
 * Otherwise, falls back to triggering external modals via jQuery.
 */
const FieldSettingsMenu = ({
  field,
  isAdmin,
  isModerator,
  showReportIssue,
  distributionId,
  distributionName,
  farmingUnitId,
  farmingUnitName,
  userName,
  userEmail,
  farmingUnitUserEmails,
  onDuplicate,
  onDelete,
  onArchive,
  onClose,
  trackEvent,
  t,
  className,
}) => {
  if (!isAdmin && !isModerator) return null;

  const [isCopied, setIsCopied] = useState(false);
  const [isDescriptionCopied, setIsDescriptionCopied] = useState(false);
  const copyTimeoutRef = useRef(null);
  const descriptionCopyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
      if (descriptionCopyTimeoutRef.current) {
        clearTimeout(descriptionCopyTimeoutRef.current);
      }
    };
  }, []);

  const closeMenu = () => {
    if (onClose) onClose();
  };

  const handleCopyFieldId = (e) => {
    e.stopPropagation();
    e.preventDefault();
    copyTextToClipboard(field.id);
    trackEvent('Copy Field ID', {
      'Field ID': field.id,
      'Field Name': field.name,
    });
    setIsCopied(true);
    if (copyTimeoutRef.current) {
      clearTimeout(copyTimeoutRef.current);
    }
    copyTimeoutRef.current = setTimeout(() => {
      setIsCopied(false);
    }, COPIED_FEEDBACK_DURATION_MS);
  };

  const handleCopyFieldDescription = (e) => {
    e.stopPropagation();
    e.preventDefault();
    const description = buildFieldDescription({
      distributionName,
      distributionId,
      farmingUnitName,
      farmingUnitId,
      fieldName: field.name,
      fieldId: field.id,
      userName,
      userEmail,
      farmingUnitUserEmails,
    });
    copyTextToClipboard(description);
    trackEvent('Copy Field Description', {
      'Field ID': field.id,
      'Field Name': field.name,
    });
    setIsDescriptionCopied(true);
    if (descriptionCopyTimeoutRef.current) {
      clearTimeout(descriptionCopyTimeoutRef.current);
    }
    descriptionCopyTimeoutRef.current = setTimeout(() => {
      setIsDescriptionCopied(false);
    }, COPIED_FEEDBACK_DURATION_MS);
  };

  const handleEditField = (e) => {
    e.stopPropagation();
    trackEvent('Edit Field', {
      'Field ID': field.id,
      'Field Name': field.name,
    });
    // Trigger modal with the clicked element as relatedTarget so modal can read data-id
    $('#fieldModalId').modal('show', e.currentTarget);
    closeMenu();
  };

  const handleMoveToArchive = (e) => {
    e.stopPropagation();
    trackEvent('Move to Archive', {
      'Field ID': field.id,
      'Field Name': field.name,
    });
    $('#approvalModalId').modal('show', e.currentTarget);
    closeMenu();
  };

  const handleDuplicate = (e) => {
    e.stopPropagation();
    if (onDuplicate) {
      onDuplicate(field);
    }
    trackEvent('Duplicate Field', {
      'Field ID': field.id,
      'Field Name': field.name,
    });
    $('#fieldModalId').modal('show', e.currentTarget);
    closeMenu();
  };

  const handleReportIssue = (e) => {
    e.stopPropagation();
    trackEvent('Report Issue', {
      'Field ID': field.id,
      'Field Name': field.name,
    });
    $('#reportIssueModalId').modal('show', e.currentTarget);
    closeMenu();
  };

  const handleDeleteField = (e) => {
    e.stopPropagation();
    trackEvent('Delete Field', {
      'Field ID': field.id,
      'Field Name': field.name,
    });
    $('#deletionApprovalModalId').modal('show', e.currentTarget);
    closeMenu();
  };

  return (
    <div
      className={`dropdown-menu text-grey45 ${className || ''}`}
      onClick={(e) => e.stopPropagation()}
    >
      {isAdmin && (
        <div
          className='dropdown-item text-grey45'
          href='#'
          onClick={handleCopyFieldId}
        >
          {isCopied ? t('dashboard_copied') : t('dashboard_copy_field_id')}
        </div>
      )}
      {isAdmin && (
        <div
          className='dropdown-item text-grey45'
          href='#'
          onClick={handleCopyFieldDescription}
        >
          {isDescriptionCopied
            ? t('dashboard_copied')
            : t('dashboard_copy_field_description')}
        </div>
      )}
      <div
        className='dropdown-item text-grey45'
        data-toggle='modal'
        data-target='#fieldModalId'
        data-id={field.id}
        href='#'
        onClick={handleEditField}
      >
        {t('dashboard_edit_field')}
      </div>

      {isAdmin && (
        <div
          className='dropdown-item text-grey45'
          data-toggle='modal'
          data-target='#approvalModalId'
          data-id={field.id}
          data-values={[distributionId, farmingUnitId, field.id]}
          href='#'
          onClick={handleMoveToArchive}
        >
          {t('dashboard_move_to_archive')}
        </div>
      )}

      {isAdmin && (
        <div
          className='dropdown-item text-grey45'
          data-toggle='modal'
          data-target='#fieldModalId'
          data-id={field.id}
          data-data='duplicate'
          href='#'
          onClick={handleDuplicate}
        >
          {t('duplicate')}
        </div>
      )}

      {showReportIssue && (
        <div
          className='dropdown-item text-grey45'
          data-toggle='modal'
          data-target='#reportIssueModalId'
          data-id={field.id}
          href='#'
          onClick={handleReportIssue}
        >
          {t('report_issue')}
        </div>
      )}

      {isAdmin && (
        <>
          <div className='dropdown-divider' />
          <div
            className='dropdown-item field-settings-menu__delete'
            data-toggle='modal'
            data-target='#deletionApprovalModalId'
            data-values={[distributionId, farmingUnitId, field.id]}
            href='#'
            onClick={handleDeleteField}
          >
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              className='field-settings-menu__delete-icon'
            >
              <path
                d='M6 7.5C6 7.22386 6.22386 7 6.5 7C6.77614 7 7 7.22386 7 7.5V11.5C7 11.7761 6.77614 12 6.5 12C6.22386 12 6 11.7761 6 11.5V7.5Z'
                fill='#F66B7A'
              />
              <path
                d='M9.5 7C9.22386 7 9 7.22386 9 7.5V11.5C9 11.7761 9.22386 12 9.5 12C9.77614 12 10 11.7761 10 11.5V7.5C10 7.22386 9.77614 7 9.5 7Z'
                fill='#F66B7A'
              />
              <path
                fillRule='evenodd'
                clipRule='evenodd'
                d='M3.7858 3V2.4C3.7858 1.75689 4.04668 1.1443 4.50429 0.695838C4.96122 0.24805 5.57672 0 6.21437 0H9.7858C10.4235 0 11.039 0.24805 11.4959 0.695838C11.9535 1.1443 12.2144 1.75689 12.2144 2.4V3H14C14.5523 3 15 3.44772 15 4C15 4.55228 14.5523 5 14 5V13.6C14 14.2431 13.7391 14.8557 13.2815 15.3042C12.8246 15.7519 12.2091 16 11.5714 16H4.42857C3.79092 16 3.17542 15.7519 2.71849 15.3042C2.26088 14.8557 2 14.2431 2 13.6V5C1.44772 5 1 4.55228 1 4C1 3.44772 1.44772 3 2 3H3.7858ZM5.90415 2.12426C5.98304 2.04695 6.09426 2 6.21437 2H9.7858C9.90591 2 10.0171 2.04695 10.096 2.12426C10.1742 2.20091 10.2144 2.30051 10.2144 2.4V3H5.7858V2.4C5.7858 2.30051 5.82594 2.20091 5.90415 2.12426ZM4 5V13.6C4 13.6995 4.04014 13.7991 4.11835 13.8757C4.19724 13.9531 4.30846 14 4.42857 14H11.5714C11.6915 14 11.8028 13.9531 11.8817 13.8757C11.9599 13.7991 12 13.6995 12 13.6V5H4Z'
                fill='#F66B7A'
              />
            </svg>
            {t('dashboard_delete_field')}
          </div>
        </>
      )}
    </div>
  );
};

FieldSettingsMenu.propTypes = {
  field: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
  }).isRequired,
  isAdmin: PropTypes.bool,
  isModerator: PropTypes.bool,
  showReportIssue: PropTypes.bool,
  distributionId: PropTypes.string,
  distributionName: PropTypes.string,
  farmingUnitId: PropTypes.string,
  farmingUnitName: PropTypes.string,
  userName: PropTypes.string,
  userEmail: PropTypes.string,
  farmingUnitUserEmails: PropTypes.arrayOf(PropTypes.string),
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func,
  onArchive: PropTypes.func,
  onClose: PropTypes.func,
  trackEvent: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  className: PropTypes.string,
};

module.exports = FieldSettingsMenu;
